import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import type { PhotoMetadata } from '@omokage/shared';
import { photoUrlKeys, fetchPhotoUrls } from './usePhotoUrls';

// Query keys for photo metadata
export const photoMetadataKeys = {
  all: ['photos', 'metadata'] as const,
};

// Response type from API
interface PhotoMetadataResponse {
  success: true;
  data: PhotoMetadata[];
}

interface UsePhotoMetadataOptions {
  /**
   * Number of initial photo URLs to prefetch (default: 60)
   * This default matches: initial visible range (30) + OVERSCAN (30) in useVisiblePhotoUrls
   */
  prefetchInitialUrls?: number;
}

/**
 * Hook to fetch all photo metadata without signed URLs.
 * Automatically prefetches initial photo URLs when metadata loads.
 *
 * @param options - Configuration options
 * @returns Query result containing photo metadata array
 */
export function usePhotoMetadata(options: UsePhotoMetadataOptions = {}) {
  const { prefetchInitialUrls = 60 } = options;
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: photoMetadataKeys.all,
    queryFn: async () => {
      const response = await apiClient.get<PhotoMetadataResponse>('/photos/metadata');
      const data = response.data;

      // Fetch initial photo URLs synchronously after metadata loads
      // This ensures URLs are available in cache before the first render completes
      if (prefetchInitialUrls > 0 && data.length > 0) {
        const initialIds = data.slice(0, prefetchInitialUrls).map((photo) => photo.id);

        // Use fetchQuery to synchronously fetch and cache URLs
        // This blocks metadata query until URLs are ready, but ensures immediate availability
        // If URL fetch fails, log error but don't fail the entire metadata query
        try {
          await queryClient.fetchQuery({
            queryKey: photoUrlKeys.byIds(initialIds),
            queryFn: () => fetchPhotoUrls(initialIds),
            staleTime: 5 * 60 * 1000, // Match usePhotoUrls
          });
        } catch (error) {
          console.error('[usePhotoMetadata] Failed to prefetch URLs:', error);
          // Continue with metadata return - URLs will be fetched on-demand by useVisiblePhotoUrls
        }
      }

      return data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes (300000ms)
  });
}
